import { axiosAuthInstance } from '../utils/getSubdomain'
import { limitPagination } from '../utils/Helper'

export const listSubject = async (
  page: number,
  searchKey?: string | number,
  limit?: string | number
) => {
  try {
    const params: any = {
      page: page,
      limit: limit ?? limitPagination,
    }
    if (searchKey) {
      params.search = searchKey
    }
    return await axiosAuthInstance.get(`/subjects/`, { params })
  } catch (error) {
    console.error('Failed to fetch teacher data:', error)
    throw error
  }
}

export const saveSubjectData = (data: FormData) => {
  return axiosAuthInstance.post('/subjects/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const deleteSubjectData = (id: string | number) => {
  return axiosAuthInstance.delete(`/subjects/${id}`)
}

export const updateSubjectData = (data: FormData, id: string | number) => {
  return axiosAuthInstance.patch(`/subjects/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const getSubject = (id: string | number) => {
  return axiosAuthInstance.get(`/subjects/${id}`)
}

export const updateLevelLabels = (
  id: string | number,
  levelLabels: string[]
) => {
  return axiosAuthInstance.patch(`/subjects/${id}/level-labels`, {
    level_labels: levelLabels,
  })
}

export type HierarchyListParams = {
  level: number
  search?: string
  parent_id?: string
  level_1_id?: string
  level_2_id?: string
  level_3_id?: string
  page?: number
  limit?: number
}

const HIERARCHY_MAX_PAGE_SIZE = 100

export const listHierarchyItems = (
  id: string | number,
  params: HierarchyListParams
) => {
  const query: Record<string, string | number> = { level: params.level }
  if (params.search) query.search = params.search
  if (params.parent_id) query.parent_id = params.parent_id
  if (params.level_1_id) query.level_1_id = params.level_1_id
  if (params.level_2_id) query.level_2_id = params.level_2_id
  if (params.level_3_id) query.level_3_id = params.level_3_id
  if (params.page) query.page = params.page
  if (params.limit) query.limit = params.limit
  return axiosAuthInstance.get(`/subjects/${id}/hierarchy/items`, { params: query })
}

/** Fetches every matching hierarchy item by paging at the API max (100). */
export const listAllHierarchyItems = async (
  id: string | number,
  params: Omit<HierarchyListParams, 'page' | 'limit'>
) => {
  const items: unknown[] = []
  let page = 1
  let totalPages = 1

  while (page <= totalPages) {
    const response = await listHierarchyItems(id, {
      ...params,
      page,
      limit: HIERARCHY_MAX_PAGE_SIZE,
    })
    const data = response?.data?.data
    items.push(...(data?.items || []))
    totalPages = data?.total_pages ?? 1
    page += 1
  }

  return items
}

export const createHierarchyItem = (
  id: string | number,
  payload: { level: number; name: string; parent_id?: string | null }
) => {
  return axiosAuthInstance.post(`/subjects/${id}/hierarchy/items`, payload)
}

export const updateHierarchyItem = (
  id: string | number,
  itemId: string,
  payload: { name?: string; parent_id?: string | null }
) => {
  return axiosAuthInstance.patch(
    `/subjects/${id}/hierarchy/items/${itemId}`,
    payload
  )
}

export const deleteHierarchyItem = (id: string | number, itemId: string) => {
  return axiosAuthInstance.delete(`/subjects/${id}/hierarchy/items/${itemId}`)
}
